import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  classifyEquipment,
  type EquipmentRow,
  type EquipCategory,
  type EquipTier,
} from "@/lib/equipment";

// ============================================================================
// REPORT-01 — Equipment installs report (first slice of the Reports surface).
//
// "How much of <equipment> did we install over <period>?" — the demand-history
// half of Seth's inventory-forecast vision (REPORT-04). Reads mirror_equipment
// install records in a date range, classifies each with the SAME lib/equipment
// classifier the Customer 360 Equipment box uses (one source of truth), and
// enriches the service city for zone grouping. The component groups + charts
// + exports client-side, so any dimension (category / make / model / zone /
// month) is a re-group, not a re-query.
//
// Data honesty: ~49% of historical equipment rows carry a legacy 1900-01-01
// (Wintac-era) install placeholder and are excluded from any dated range — we
// surface that caveat in the UI rather than silently dropping it.
// ============================================================================

const PAGE = 1000; // PostgREST max rows per request
const MAX_ROWS = 20000; // safety cap; wide ranges (e.g. "all time") truncate + warn
const CITY_CHUNK = 300; // custnos per mirror_location lookup (URL-length safe)

export interface EquipInstallRow {
  custno: string;
  locno: string;
  counter: string;
  mfg: string | null;
  model: string | null;
  install_date: string; // YYYY-MM-DD (always present — range-filtered)
  tier: EquipTier;
  category: EquipCategory;
  categoryLabel: string;
  city: string | null;
}

export interface EquipInstallsResult {
  rows: EquipInstallRow[];
  truncated: boolean; // hit MAX_ROWS — totals are a floor, narrow the range
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Fetch every equipment row installed within [fromISO, toISO], paginated.
async function fetchInstallRows(
  fromISO: string,
  toISO: string,
): Promise<{ raw: (EquipmentRow & { custno: string; locno: string })[]; truncated: boolean }> {
  const cols =
    "custno, locno, counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, custom_1, custom_2, invoice";
  const raw: (EquipmentRow & { custno: string; locno: string })[] = [];
  for (let page = 0; page * PAGE < MAX_ROWS; page++) {
    const start = page * PAGE;
    const { data, error } = await supabase
      .from("mirror_equipment")
      .select(cols)
      .gte("install_date", fromISO)
      .lte("install_date", toISO)
      .order("install_date", { ascending: true })
      .range(start, start + PAGE - 1);
    if (error) throw error;
    const batch = (data ?? []) as (EquipmentRow & { custno: string; locno: string })[];
    raw.push(...batch);
    if (batch.length < PAGE) return { raw, truncated: false };
  }
  return { raw, truncated: true };
}

// (custno|locno) -> service city, for the zone grouping. Chunked so a big range
// doesn't blow the URL length on the .in() filter.
async function fetchCityMap(custnos: string[]): Promise<Map<string, string | null>> {
  const m = new Map<string, string | null>();
  for (const part of chunk(custnos, CITY_CHUNK)) {
    const { data } = await supabase
      .from("mirror_location")
      .select("custno, locno, city")
      .in("custno", part);
    for (const l of (data ?? []) as { custno: string; locno: string; city: string | null }[]) {
      m.set(`${l.custno}|${l.locno}`, l.city);
    }
  }
  return m;
}

export function useEquipmentInstalls(fromISO: string, toISO: string) {
  return useQuery({
    queryKey: ["equipment_installs", fromISO, toISO],
    enabled: !!fromISO && !!toISO,
    queryFn: async (): Promise<EquipInstallsResult> => {
      const { raw, truncated } = await fetchInstallRows(fromISO, toISO);
      if (raw.length === 0) return { rows: [], truncated };

      const custnos = Array.from(new Set(raw.map((r) => r.custno)));
      const cityMap = await fetchCityMap(custnos);

      const rows: EquipInstallRow[] = raw.map((r) => {
        const { tier, category, categoryLabel } = classifyEquipment(r);
        return {
          custno: r.custno,
          locno: r.locno,
          counter: r.counter,
          mfg: r.mfg,
          model: r.model,
          install_date: r.install_date as string,
          tier,
          category,
          categoryLabel,
          city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
        };
      });
      return { rows, truncated };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// REPORT-04 (phase b) — current stock on hand, keyed by coreModel() match key.
// Seeded from the Sales INSTOCK snapshot (ESC inventory is unreliable). The
// seasonal report joins this to projected demand to compute the order target.
// ----------------------------------------------------------------------------
export interface StockRow {
  model_core: string;
  model_label: string;
  qty: number;
  bin: string | null;
  category: string | null;
  source: string;
  as_of: string;
}
export interface StockSnapshot {
  byCore: Map<string, StockRow>;
  totalUnits: number;
  asOf: string | null;
  source: string | null;
}

export function useStockOnHand() {
  return useQuery({
    queryKey: ["stock_on_hand"],
    queryFn: async (): Promise<StockSnapshot> => {
      const { data, error } = await supabase
        .from("stock_on_hand")
        .select("model_core, model_label, qty, bin, category, source, as_of");
      if (error) throw error;
      const rows = (data ?? []) as StockRow[];
      const byCore = new Map<string, StockRow>();
      let totalUnits = 0;
      let asOf: string | null = null;
      let source: string | null = null;
      for (const r of rows) {
        byCore.set(r.model_core, r);
        totalUnits += r.qty;
        asOf = r.as_of;
        source = r.source;
      }
      return { byCore, totalUnits, asOf, source };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Department label resolver now lives in @/lib/departments (shared with the 360,
// invoice peek, and tech payroll so a dept name is never re-hardcoded). Imported
// here for internal use (deptLabel below) and re-exported for existing consumers
// that import { DEPT_LABEL, useDepartments, deptLabel } from "./api".
import { DEPT_LABEL, useDepartments, deptLabel } from "@/lib/departments";
export { DEPT_LABEL, useDepartments, deptLabel };

// ----------------------------------------------------------------------------
// REPORT (Revenue & sales) — mirror_sales rows in a date range. inv_amount is
// the invoice total; labor/mat/other_cost are the cost split. Paginated like
// the equipment report; grouped/charted client-side.
// ----------------------------------------------------------------------------
const PAGE2 = 1000;
const MAX_ROWS2 = 30000;

export interface SaleRow {
  invoice: string;
  inv_date: string;
  dept: string | null;
  salesman: string | null;
  inv_amount: number;
  labor_cost: number;
  mat_cost: number;
  other_cost: number;
  city: string | null;
}
export interface RevenueResult { rows: SaleRow[]; truncated: boolean }

export function useRevenue(fromISO: string, toISO: string) {
  return useQuery({
    queryKey: ["revenue_sales", fromISO, toISO],
    enabled: !!fromISO && !!toISO,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RevenueResult> => {
      const raw: Array<{ invoice: string; inv_date: string | null; dept: string | null; salesman: string | null; inv_amount: number | null; labor_cost: number | null; mat_cost: number | null; other_cost: number | null; custno: string; locno: string }> = [];
      let truncated = false;
      for (let page = 0; page * PAGE2 < MAX_ROWS2; page++) {
        const start = page * PAGE2;
        const { data, error } = await supabase
          .from("mirror_sales")
          .select("invoice, inv_date, dept, salesman, inv_amount, labor_cost, mat_cost, other_cost, custno, locno")
          .gte("inv_date", fromISO).lte("inv_date", toISO)
          .order("inv_date", { ascending: true })
          .range(start, start + PAGE2 - 1);
        if (error) throw error;
        const batch = data ?? [];
        raw.push(...(batch as typeof raw));
        if (batch.length < PAGE2) { truncated = false; break; }
        if ((page + 1) * PAGE2 >= MAX_ROWS2) truncated = true;
      }
      if (raw.length === 0) return { rows: [], truncated };
      // Zone enrichment (chunked) like the equipment report.
      const custnos = Array.from(new Set(raw.map((r) => r.custno)));
      const cityMap = new Map<string, string | null>();
      for (let i = 0; i < custnos.length; i += 300) {
        const part = custnos.slice(i, i + 300);
        const { data } = await supabase.from("mirror_location").select("custno, locno, city").in("custno", part);
        for (const l of (data ?? []) as { custno: string; locno: string; city: string | null }[]) {
          cityMap.set(`${l.custno}|${l.locno}`, l.city);
        }
      }
      const rows: SaleRow[] = raw.map((r) => ({
        invoice: r.invoice,
        inv_date: r.inv_date as string,
        dept: r.dept,
        salesman: r.salesman,
        inv_amount: Number(r.inv_amount ?? 0),
        labor_cost: Number(r.labor_cost ?? 0),
        mat_cost: Number(r.mat_cost ?? 0),
        other_cost: Number(r.other_cost ?? 0),
        city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
      }));
      return { rows, truncated };
    },
  });
}

// ----------------------------------------------------------------------------
// REPORT (Dispatch volume) — v_dispatch_full rows in a date range (all depts).
// ----------------------------------------------------------------------------
export interface DispatchVolRow {
  dispatch: string;
  appointment_date: string;
  tech_empno: string | null;
  tech_name: string | null;
  tech_dept: string | null;
  city: string | null;
  window_code: string | null;
  status: string | null;
}
export interface DispatchVolResult { rows: DispatchVolRow[]; truncated: boolean }

export function useDispatchVolume(fromISO: string, toISO: string) {
  return useQuery({
    queryKey: ["dispatch_volume", fromISO, toISO],
    enabled: !!fromISO && !!toISO,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DispatchVolResult> => {
      const rows: DispatchVolRow[] = [];
      let truncated = false;
      for (let page = 0; page * PAGE2 < MAX_ROWS2; page++) {
        const start = page * PAGE2;
        const { data, error } = await supabase
          .from("v_dispatch_full")
          .select("dispatch, appointment_date, tech_empno, tech_name, tech_dept, city, window_code, status")
          .gte("appointment_date", fromISO).lte("appointment_date", toISO)
          .order("appointment_date", { ascending: true })
          .range(start, start + PAGE2 - 1);
        if (error) throw error;
        const batch = (data ?? []) as DispatchVolRow[];
        rows.push(...batch);
        if (batch.length < PAGE2) { truncated = false; break; }
        if ((page + 1) * PAGE2 >= MAX_ROWS2) truncated = true;
      }
      return { rows, truncated };
    },
  });
}

// ----------------------------------------------------------------------------
// REPORT-07 — inactive tech empnos (mirror_employee.inactive). Used to tag +
// optionally hide inactive techs in the dispatch report ("mark but keep").
// Employment is a trusted stated flag (the boundary case of derive-don't-flag);
// we trust it here and let activity audit it elsewhere (a future Tech 360).
// ----------------------------------------------------------------------------
export function useInactiveTechEmpnos() {
  return useQuery({
    queryKey: ["inactive_tech_empnos"],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("mirror_employee").select("empno, inactive").eq("inactive", true);
      if (error) throw error;
      return new Set(((data ?? []) as { empno: string }[]).map((r) => (r.empno ?? "").trim()));
    },
  });
}

// ----------------------------------------------------------------------------
// REPORT-01 — the report-builder engine, first entity = Agreements (DEC-24 v1
// scope). Fetches all agreement headers once + derives status / year fields;
// the CustomReport component then filters / groups / aggregates client-side
// from a saved query definition. The same definition is what SEG-01 will save.
// No enrichment in v1 (status/plan/value/dept/dates only) — zone + customer
// name come with the next slice (chunked lookup).
// ----------------------------------------------------------------------------
export interface AgrReportRow {
  custno: string;
  locno: string;
  customer: string;
  agrmt_no: string;
  status: "active" | "expiring" | "expired" | "inactive";
  plan: string;
  type: string;
  dept: string;
  zone: string;
  value: number;
  signed: string | null;
  expires: string | null;
  lastSrvc: string | null;
  expireYear: string;
  signedYear: string;
}

export function useAllAgreements(enabled = true) {
  return useQuery({
    queryKey: ["all_agreements_report"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AgrReportRow[]> => {
      const PG = 1000;
      const today = new Date().toISOString().slice(0, 10);
      const soon = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
      const out: AgrReportRow[] = [];
      for (let page = 0; page < 10; page++) {
        const start = page * PG;
        const { data, error } = await supabase
          .from("mirror_serv_agr")
          .select("custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, dept, orig_contr, expire_date, last_srvc, sa_inactive")
          .order("agrmt_no", { ascending: true })
          .range(start, start + PG - 1);
        if (error) throw error;
        const batch = (data ?? []) as Array<{
          custno: string; locno: string; agrmt_no: string; agrmt_type: string | null; contr_per: string | null;
          contr_amt: number | null; dept: string | null; orig_contr: string | null;
          expire_date: string | null; last_srvc: string | null; sa_inactive: number | null;
        }>;
        for (const r of batch) {
          const exp = r.expire_date;
          const inactive = r.sa_inactive != null && r.sa_inactive !== 0;
          const status: AgrReportRow["status"] =
            inactive ? "inactive"
              : exp && exp < today ? "expired"
                : exp && exp <= soon ? "expiring"
                  : "active";
          out.push({
            custno: r.custno,
            locno: r.locno,
            customer: r.custno,
            agrmt_no: r.agrmt_no,
            status,
            plan: (r.contr_per ?? r.agrmt_type ?? "—").trim() || "—",
            type: (r.agrmt_type ?? "—").trim() || "—",
            dept: deptLabel(r.dept),
            zone: "—",
            value: Number(r.contr_amt ?? 0),
            signed: r.orig_contr,
            expires: exp,
            lastSrvc: r.last_srvc,
            expireYear: exp ? exp.slice(0, 4) : "—",
            signedYear: r.orig_contr ? r.orig_contr.slice(0, 4) : "—",
          });
        }
        if (batch.length < PG) break;
      }
      // Zone (service city) + customer-name enrichment — chunked lookups run in
      // parallel so a few thousand agreements resolve fast (cached 5 min).
      const custnos = Array.from(new Set(out.map((r) => r.custno)));
      const chunks: string[][] = [];
      for (let i = 0; i < custnos.length; i += 500) chunks.push(custnos.slice(i, i + 500));
      const [locResults, custResults] = await Promise.all([
        Promise.all(chunks.map((p) => supabase.from("mirror_location").select("custno, locno, city").in("custno", p))),
        Promise.all(chunks.map((p) => supabase.from("mirror_customer").select("custno, fullname").in("custno", p))),
      ]);
      const cityByLoc = new Map<string, string | null>();
      for (const { data: locs } of locResults) {
        for (const l of (locs ?? []) as { custno: string; locno: string; city: string | null }[]) {
          cityByLoc.set(`${l.custno}|${l.locno}`, l.city);
        }
      }
      const nameByCust = new Map<string, string | null>();
      for (const { data: cs } of custResults) {
        for (const c of (cs ?? []) as { custno: string; fullname: string | null }[]) {
          nameByCust.set(c.custno, c.fullname);
        }
      }
      for (const r of out) {
        r.zone = (cityByLoc.get(`${r.custno}|${r.locno}`) ?? "").trim() || "—";
        r.customer = (nameByCust.get(r.custno) ?? "").trim() || r.custno;
      }
      return out;
    },
  });
}

// ----------------------------------------------------------------------------
// SEG-01 / DEC-24 — saved segments. A segment = a named, reusable query def
// (the exact shape REPORT-01's builder produces). Pegasus-native `segment`
// table; "filter once → reuse it as a worklist / route / message" rides on this.
// ----------------------------------------------------------------------------
export interface Segment {
  id: string;
  name: string;
  entity: string;
  definition: unknown;
  description: string | null;
  created_by: string | null;
  created_at: string;
}

export function useSegments(entity?: string) {
  return useQuery({
    queryKey: ["segments", entity ?? "all"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Segment[]> => {
      let q = supabase
        .from("segment")
        .select("id, name, entity, definition, description, created_by, created_at")
        .order("created_at", { ascending: false });
      if (entity) q = q.eq("entity", entity);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Segment[];
    },
  });
}

export function useSaveSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; entity: string; definition: unknown; description?: string }) => {
      const { data: auth } = await supabase.auth.getUser();
      const created_by = auth?.user?.email ?? auth?.user?.id ?? null;
      const { error } = await supabase.from("segment").insert({
        name: input.name,
        entity: input.entity,
        definition: input.definition,
        description: input.description ?? null,
        created_by,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["segments"] }),
  });
}

export function useDeleteSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("segment").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["segments"] }),
  });
}

// ----------------------------------------------------------------------------
// REPORT-05 — AI-suggested business insights. The business-insights Edge
// Function computes real signals from the mirror (revenue YoY by dept, R-454B
// ramp, owed-visit $, top install zone) then has Gemini narrate the computed
// facts (numbers are ours; AI only writes prose). Each insight carries its raw
// metric so nothing is unverifiable.
// ----------------------------------------------------------------------------
export interface InsightFact { id: string; title: string; detail: string; metric: string }
export interface InsightsResult { facts: InsightFact[]; narrative: string; generated_at: string | null }

export function useBusinessInsights() {
  return useQuery({
    queryKey: ["business_insights"],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<InsightsResult> => {
      // v8 (Wave-1): the fn serves its PERSISTED briefing instantly (24h cache in
      // org_setting) — Reports never gates on a Gemini call again.
      const { data, error } = await supabase.functions.invoke("business-insights", { body: {} });
      if (error) throw error;
      return {
        facts: (data?.facts ?? []) as InsightFact[],
        narrative: data?.narrative ?? "",
        generated_at: data?.generated_at ?? null,
      };
    },
  });
}

/** Wave-1: the Refresh button — forces a REGENERATE (skips the 24h server cache),
 *  then swaps the fresh briefing into the query cache. */
export function useRefreshInsights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<InsightsResult> => {
      const { data, error } = await supabase.functions.invoke("business-insights", { body: { refresh: true } });
      if (error) throw error;
      return {
        facts: (data?.facts ?? []) as InsightFact[],
        narrative: data?.narrative ?? "",
        generated_at: data?.generated_at ?? null,
      };
    },
    onSuccess: (d) => qc.setQueryData(["business_insights"], d),
  });
}
